import {
  LogDocument,
  LoggerInterface,
  LoggerOptions,
  LogLevel,
  LogLevelEnum,
  LogTransportInterface,
} from "./LoggerTypes";

export class Logger implements LoggerInterface {
  private configLevel: LogLevel;

  private transports: LogTransportInterface[];

  private versionInfo: { version: string; buildTimestamp: null };

  constructor(private options: LoggerOptions) {
    this.configLevel = new LogLevel(options.configLevel);
    this.transports = options.transports;
  }

  debug(
    message: string,
    tags: string[],
    logData?: { [key: string]: unknown },
  ): Promise<boolean> {
    return this.logDocument(LogLevelEnum.DEBUG, message, tags, logData);
  }

  log(
    message: string,
    tags: string[],
    logData?: { [key: string]: unknown },
  ): Promise<boolean> {
    return this.logDocument(LogLevelEnum.LOG, message, tags, logData);
  }

  warn(
    message: string,
    tags: string[],
    logData?: { [key: string]: unknown },
  ): Promise<boolean> {
    return this.logDocument(LogLevelEnum.WARN, message, tags, logData);
  }

  error(
    message: string,
    tags: string[],
    logData?: { [key: string]: unknown },
  ): Promise<boolean> {
    return this.logDocument(LogLevelEnum.ERROR, message, tags, logData);
  }

  critical(
    message: string,
    tags: string[],
    logData?: { [key: string]: unknown },
  ): Promise<boolean> {
    return this.logDocument(LogLevelEnum.CRITICAL, message, tags, logData);
  }

  private async logDocument(
    level: LogLevelEnum,
    message: string,
    tags: string[],
    logData?: { [key: string]: unknown },
  ): Promise<boolean> {
    const logLevel = new LogLevel(level);
    if (!logLevel.isActive(this.configLevel)) {
      return true;
    }
    if (!this.versionInfo) {
      this.versionInfo = await this.getVersionInfo();
    }
    const doc: LogDocument = {
      level: logLevel,
      timestamp: new Date(),
      logMessage: message,
      logTags: tags,
      deviceInfo: this.options.deviceInfo,
      deviceId: this.options.deviceId,
      family: this.options.family,
      env: this.options.env,
      version: this.versionInfo.version,
      logData,
    };
    for (const transport of this.transports) {
      // eslint-disable-next-line no-await-in-loop
      if (!(await transport.logDocument(doc))) {
        return false;
      }
    }
    return true;
  }

  private async getVersionInfo() {
    try {
      const response = await fetch("/infoVersion.json");
      if (!response.ok) {
        throw new Error(response.statusText);
      }
      return await response.json();
    } catch (_e) {
      return { version: "unknown" };
    }
  }
}
